/** Cards on a facilities work order: where the work is, its checklist, and its costs. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckSquare, Plus, Square, Trash2 } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import { COST_KIND_LABEL, equipmentLabel, type WorkOrderCostKind, type WorkOrderCosts } from '../lib/facilities';
import { formatDate, formatMinutes, formatMoney, formatUtcDate, toDateInput } from '../lib/format';
import type { ChecklistItem, WorkOrderDetail } from '../lib/types';
import { EquipmentSelect, PlaceFields } from './facilities-ui';
import { useToast } from './toast';
import { Alert, Button, Card, CardHeader, cn, Field, Input, Modal, Select } from './ui';

export interface LocationUpdate {
  buildingId: string | null;
  roomId: string | null;
  locationDetail: string | null;
  facilityAssetId: string | null;
}

/** Where the work is needed and the equipment it's on, changeable by technicians. */
export function LocationCard({ ticket, onUpdate, saving }: { ticket: WorkOrderDetail; onUpdate(body: LocationUpdate, done: () => void): void; saving: boolean }) {
  const canEdit = ticket.permissions.update;
  const staff = ticket.permissions.viewAsset;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ buildingId: '', roomId: '', locationDetail: '', facilityAssetId: '' });

  useEffect(() => {
    if (!editing) return;
    setDraft({
      buildingId: ticket.buildingId ?? ticket.room?.building.id ?? '',
      roomId: ticket.roomId ?? '',
      locationDetail: ticket.locationDetail ?? '',
      facilityAssetId: ticket.facilityAssetId ?? '',
    });
  }, [editing, ticket]);

  const building = ticket.building ?? ticket.room?.building ?? null;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    onUpdate(
      {
        buildingId: draft.buildingId || null,
        roomId: draft.roomId || null,
        locationDetail: draft.locationDetail.trim() || null,
        facilityAssetId: draft.facilityAssetId || null,
      },
      () => setEditing(false),
    );
  };

  return (
    <Card>
      <CardHeader
        title="Where"
        actions={
          canEdit &&
          !editing && (
            <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
              Change
            </Button>
          )
        }
      />
      {editing ? (
        <form onSubmit={submit} className="space-y-3 p-5">
          <PlaceFields
            requireBuilding
            buildingId={draft.buildingId}
            roomId={draft.roomId}
            onChange={(next) => setDraft((current) => ({ ...current, ...next, facilityAssetId: next.buildingId === current.buildingId ? current.facilityAssetId : '' }))}
          />
          <Field label="Location details" hint="e.g. north stairwell, by the gym doors">
            <Input value={draft.locationDetail} maxLength={200} onChange={(e) => setDraft({ ...draft, locationDetail: e.target.value })} />
          </Field>
          <Field label="Equipment">
            <EquipmentSelect value={draft.facilityAssetId} buildingId={draft.buildingId || undefined} onChange={(facilityAssetId) => setDraft({ ...draft, facilityAssetId })} />
          </Field>
          <div className="flex justify-end gap-2">
            <Button onClick={() => setEditing(false)}>Cancel</Button>
            <Button type="submit" variant="primary" loading={saving} disabled={!draft.buildingId}>
              Save
            </Button>
          </div>
        </form>
      ) : (
        <div className="space-y-1 p-5 text-sm">
          <p className="font-medium text-slate-900">{building?.name ?? 'No building'}</p>
          {ticket.room && <p className="text-slate-700">{ticket.room.name}</p>}
          {ticket.locationDetail && <p className="text-slate-600">{ticket.locationDetail}</p>}
          {ticket.facilityAsset && (
            <p className="pt-2 text-xs text-slate-500">
              Equipment:{' '}
              {staff ? (
                <Link to={`/facilities/equipment/${ticket.facilityAsset.id}`} className="text-brand-700 hover:underline">
                  {equipmentLabel(ticket.facilityAsset)}
                </Link>
              ) : (
                equipmentLabel(ticket.facilityAsset)
              )}
            </p>
          )}
        </div>
      )}
    </Card>
  );
}

/** Steps to finish the work, such as a preventive maintenance checklist. */
export function ChecklistCard({ ticket }: { ticket: WorkOrderDetail }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [label, setLabel] = useState('');
  const canEdit = ticket.permissions.editChecklist;
  const items = ticket.checklistItems;

  const setItems = (next: ChecklistItem[]) =>
    queryClient.setQueryData<WorkOrderDetail>(['work-order', ticket.id], (current) => (current ? { ...current, checklistItems: next } : current));
  const onError = (err: unknown) => toast.error(errorMessage(err));
  const toggle = useMutation({
    mutationFn: (item: ChecklistItem) => api.patch<ChecklistItem[]>(`/work-orders/${ticket.id}/checklist/${item.id}`, { done: !item.doneAt }),
    onSuccess: setItems,
    onError,
  });
  const add = useMutation({
    mutationFn: () => api.post<ChecklistItem[]>(`/work-orders/${ticket.id}/checklist`, { label: label.trim() }),
    onSuccess: (next) => {
      setItems(next);
      setLabel('');
    },
    onError,
  });
  const remove = useMutation({
    mutationFn: (itemId: string) => api.delete<ChecklistItem[]>(`/work-orders/${ticket.id}/checklist/${itemId}`),
    onSuccess: setItems,
    onError,
  });

  if (!canEdit && items.length === 0) return null;
  const done = items.filter((item) => item.doneAt).length;

  return (
    <Card>
      <CardHeader title="Checklist" description={items.length ? `${done} of ${items.length} done` : 'Steps to finish this work'} />
      {items.length > 0 && (
        <div className="px-5 pt-3">
          <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${(done / items.length) * 100}%` }} />
          </div>
        </div>
      )}
      {items.length > 0 && (
        <ul className="px-2 py-2">
          {items.map((item) => (
            <li key={item.id} className="group flex items-start gap-2 rounded-md px-3 py-2 hover:bg-slate-50">
              <button
                type="button"
                disabled={!canEdit || toggle.isPending}
                onClick={() => toggle.mutate(item)}
                className="mt-0.5 text-slate-400 hover:text-brand-700 disabled:cursor-default disabled:hover:text-slate-400"
                aria-label={item.doneAt ? `Mark “${item.label}” not done` : `Mark “${item.label}” done`}
              >
                {item.doneAt ? <CheckSquare className="size-4 text-emerald-600" /> : <Square className="size-4" />}
              </button>
              <div className="min-w-0 flex-1">
                <p className={cn('text-sm', item.doneAt ? 'text-slate-400 line-through' : 'text-slate-800')}>{item.label}</p>
                {item.doneAt && (
                  <p className="text-xs text-slate-400">
                    {item.doneByName} · {formatDate(item.doneAt)}
                  </p>
                )}
              </div>
              {canEdit && (
                <button
                  type="button"
                  onClick={() => remove.mutate(item.id)}
                  className="text-slate-300 opacity-0 group-hover:opacity-100 hover:text-red-600 focus:opacity-100"
                  aria-label={`Remove “${item.label}”`}
                >
                  <Trash2 className="size-4" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {canEdit && (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (label.trim()) add.mutate();
          }}
          className="flex gap-2 border-t border-slate-100 p-3"
        >
          <Input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={200} placeholder="Add a step" aria-label="New checklist step" />
          <Button type="submit" size="sm" icon={<Plus />} disabled={!label.trim()} loading={add.isPending}>
            Add
          </Button>
        </form>
      )}
    </Card>
  );
}

const COST_KINDS: WorkOrderCostKind[] = ['MATERIALS', 'CONTRACTOR', 'EQUIPMENT', 'OTHER'];

/** Materials, contractors, and equipment on a work order, with labor from the time logged. */
export function CostsCard({ ticketId }: { ticketId: string }) {
  const { me } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const query = useQuery({ queryKey: ['work-order-costs', ticketId], queryFn: () => api.get<WorkOrderCosts>(`/work-orders/${ticketId}/costs`) });

  const saved = (next: WorkOrderCosts) => {
    queryClient.setQueryData(['work-order-costs', ticketId], next);
    void queryClient.invalidateQueries({ queryKey: ['facilities'] });
  };
  const remove = useMutation({
    mutationFn: (costId: string) => api.delete<WorkOrderCosts>(`/work-orders/${ticketId}/costs/${costId}`),
    onSuccess: (next) => {
      saved(next);
      toast.success('Cost removed');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const data = query.data;

  return (
    <Card>
      <CardHeader
        title="Costs"
        description="Materials, contractors, and equipment, plus labor from logged time"
        actions={
          data?.permissions.add && (
            <Button size="sm" icon={<Plus />} onClick={() => setAdding(true)}>
              Add
            </Button>
          )
        }
      />
      {query.isLoading ? (
        <p className="px-5 py-4 text-sm text-slate-500">Loading costs…</p>
      ) : query.error ? (
        <p className="px-5 py-4 text-sm text-red-600">{errorMessage(query.error)}</p>
      ) : (
        data && (
          <>
            <ul className="divide-y divide-slate-100">
              {data.costs.map((cost) => (
                <li key={cost.id} className="group flex items-start gap-3 px-5 py-2.5 text-sm">
                  <div className="min-w-0 flex-1">
                    <p className="text-slate-800">{cost.description}</p>
                    <p className="text-xs text-slate-500">
                      {[COST_KIND_LABEL[cost.kind], cost.vendor, formatUtcDate(cost.incurredOn), cost.createdByName].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                  <span className="font-medium text-slate-900 tabular-nums">{formatMoney(cost.amount)}</span>
                  {data.permissions.add && (data.permissions.deleteAny || cost.createdById === me?.user.id) && (
                    <button
                      type="button"
                      onClick={() => remove.mutate(cost.id)}
                      className="text-slate-300 opacity-0 group-hover:opacity-100 hover:text-red-600 focus:opacity-100"
                      aria-label={`Remove ${cost.description}`}
                    >
                      <Trash2 className="size-4" />
                    </button>
                  )}
                </li>
              ))}
              <li className="flex items-start gap-3 px-5 py-2.5 text-sm">
                <div className="min-w-0 flex-1">
                  <p className="text-slate-800">Labor</p>
                  <p className="text-xs text-slate-500">
                    {data.labor.minutes ? formatMinutes(data.labor.minutes) : 'No time logged yet'}
                    {data.labor.hourlyRate === null ? ' · set an hourly rate in Facilities setup to count its cost' : ` at ${formatMoney(data.labor.hourlyRate)} an hour`}
                  </p>
                </div>
                <span className="font-medium text-slate-900 tabular-nums">{data.labor.cost === null ? '—' : formatMoney(data.labor.cost)}</span>
              </li>
            </ul>
            <div className="flex items-center justify-between border-t border-slate-100 px-5 py-3 text-sm font-semibold text-slate-900">
              <span>Total</span>
              <span className="tabular-nums">{formatMoney(data.totals.total)}</span>
            </div>
          </>
        )
      )}
      <CostModal open={adding} onClose={() => setAdding(false)} ticketId={ticketId} onSaved={saved} />
    </Card>
  );
}

function CostModal({ open, onClose, ticketId, onSaved }: { open: boolean; onClose(): void; ticketId: string; onSaved(costs: WorkOrderCosts): void }) {
  const toast = useToast();
  const [form, setForm] = useState({ kind: 'MATERIALS' as WorkOrderCostKind, description: '', vendor: '', amount: '', incurredOn: '' });
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setForm({ kind: 'MATERIALS', description: '', vendor: '', amount: '', incurredOn: toDateInput(new Date()) });
    setError('');
  }, [open]);

  const save = useMutation({
    mutationFn: () =>
      api.post<WorkOrderCosts>(`/work-orders/${ticketId}/costs`, {
        kind: form.kind,
        description: form.description.trim(),
        vendor: form.vendor.trim() || null,
        amount: Number(form.amount),
        incurredOn: form.incurredOn || undefined,
      }),
    onSuccess: (next) => {
      onSaved(next);
      toast.success('Cost added');
      onClose();
    },
    onError: (err) => setError(errorMessage(err)),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError('');
    const amount = Number(form.amount);
    if (form.amount.trim() === '' || Number.isNaN(amount) || amount < 0) {
      setError('Enter the amount, like 125.50');
      return;
    }
    save.mutate();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add a cost"
      description="Materials, a contractor’s invoice, rented equipment, or anything else this work cost. Labor comes from logged time."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form="cost-form" loading={save.isPending}>
            Add cost
          </Button>
        </>
      }
    >
      <form id="cost-form" onSubmit={submit} className="space-y-4">
        {error && <Alert tone="red">{error}</Alert>}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Type">
            <Select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value as WorkOrderCostKind })}>
              {COST_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {COST_KIND_LABEL[kind]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Amount" required>
            <Input type="number" min={0} step="0.01" inputMode="decimal" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0.00" />
          </Field>
        </div>
        <Field label="What was it?" required>
          <Input value={form.description} maxLength={200} required onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g. Replacement faucet cartridge" />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Vendor">
            <Input value={form.vendor} maxLength={150} onChange={(e) => setForm({ ...form, vendor: e.target.value })} />
          </Field>
          <Field label="Date">
            <Input type="date" value={form.incurredOn} onChange={(e) => setForm({ ...form, incurredOn: e.target.value })} />
          </Field>
        </div>
      </form>
    </Modal>
  );
}
